import styled, { css, keyframes } from 'styled-components';
import { layout, media, colorUtils } from '@prisjakt/ui-base';

import Clickable from '@prisjakt/ui-clickable';
import { TitleSmallText } from '@prisjakt/ui/text/variants';
import CloseIcon from 'mdi-react/CloseIcon';
import SearchIconComponent from 'mdi-react/SearchIcon';

const { spacingNormal } = layout.measurements;

const BUTTON_MIN = '40px';
const BUTTON_MAX = '100px';

const scaleIn = keyframes`
  from {
    width: ${BUTTON_MIN};
  }

  to {
    width: ${BUTTON_MAX};
  }
`;

const scaleOut = keyframes`
  from {
    width: ${BUTTON_MAX};
  }

  to {
    width: ${BUTTON_MIN};
  }
`;

export const SearchButton = styled(Clickable)<{
  disabled: boolean;
}>(
  p => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    background-color: ${p.theme.colors.yellove500};
    height: 100%;
    padding: 0 ${layout.measurements.spacingNormal};
    user-select: none;
    cursor: ${p.disabled ? 'initial' : 'pointer'};
    width: ${BUTTON_MIN};
    transition: background-color 150ms ease;

    &:hover {
      background-color: ${colorUtils.shadeColor(
        p.theme.colors.yellove500,
        p.disabled ? 0 : -10
      )};
    }

    ${media.mediumUp`
      ${p.disabled &&
        css`
          animation: ${scaleOut} 150ms;
          width: ${BUTTON_MIN};
        `}

      ${!p.disabled &&
        css`
          width: ${BUTTON_MAX};
          animation: ${scaleIn} 150ms;
        `}
    `};
  `
);

export const StyledSearchBar = styled.div<{ $focused: boolean }>(
  p => css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: ${layout.searchBar.height.desktop.rem};
    overflow: hidden;
    position: relative;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(#000, 0.2);
    background: ${p.theme.colors.base0};
  `
);

export const InputWrapper = styled.div`
  overflow: hidden;
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  opacity: 1;
`;

export const SearchInput = styled.input`
  background: transparent;
  border: none;
  width: 100%;
  height: ${layout.searchBar.height.desktop.rem};
  flex-grow: 1;
  padding: 0 ${spacingNormal};

  &:focus {
    outline: none;
  }
`;

export const ClearIcon = styled(CloseIcon)``;

type ClearButtonProps = {
  show?: boolean;
};

export const ClearButton = styled(Clickable)<ClearButtonProps>`
  display: ${p => (p.show ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  background-color: ${p => p.theme.bg.tertiary};
  color: ${p => p.theme.icons.primary};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  margin-right: ${spacingNormal};
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover {
    background-color: ${p => colorUtils.shadeColor(p.theme.bg.tertiary, -20)};
  }
`;

export const SearchIcon = styled(SearchIconComponent)``;

export const SearchButtonText = styled(TitleSmallText)`
  > * {
    align-items: center;
    justify-content: center;
  }
`;

export const Text = styled.span<{ $visible: boolean }>`
  display: ${p => (p.$visible ? 'inline' : 'none')};

  ${media.smallOnly`
    display: none;
  `};
`;
